import { TLocale } from '@internal/utils/machine/i18n/Locale'
import { Machine } from '@internal/utils/machine/Machine'
import { getMediaSrc, getMediaUrl } from '@internal/utils/media/getMediaSrc'
import { MachineMedia } from '@internal/utils/media/Media'
import {
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
} from '@mui/icons-material'
import { IconButton, Typography } from '@mui/material'
import classNames from 'classnames'
import 'lazysizes'
import React, { useCallback, useMemo, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'
import { Keyboard, Mousewheel, Navigation, Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { MediaSliderOverlay } from '../MediaSliderOverlay/MediaSliderOverlay'
import { ShareButton } from '../ShareButton/ShareButton'
import styles from './thumbnailSlider.module.scss'

export type ThumbnailSliderProps = {
  media: MachineMedia[]
  imageRatio: number
  machine: Machine
  locale: TLocale
  slidesWrapperClassName?: string
  hasMediaOverlay?: boolean
  pagination?: boolean
  hideShareButton?: boolean
}

export const ThumbnailSlider: React.FC<ThumbnailSliderProps> = React.memo(
  (props) => {
    const [isMediaOverlayOpen, setIsMediaOverlayOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState<number>(0)

    const handleSlideChange = useCallback(
      (swiper: SwiperType) => setActiveIndex(swiper.activeIndex),
      []
    )

    const handleMainSliderClick = useCallback(() => {
      if (props.hasMediaOverlay) {
        setIsMediaOverlayOpen(true)
      }
    }, [props.hasMediaOverlay])

    const handleCloseMediaOverlay = useCallback(
      () => setIsMediaOverlayOpen(false),
      []
    )

    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)

    const { images, thumbnails } = useMemo(() => {
      return {
        images: props.media.map((media) => {
          const ratio =
            typeof media.ratio === 'number' && media.ratio <= 1
              ? 'original'
              : props.imageRatio
          const mediaSrc = getMediaSrc(media, {
            ratio,
            sizeSet: 'medium',
          })

          return {
            mediaSrc,
            ratio,
          }
        }),
        thumbnails: props.media.map((media) =>
          getMediaUrl(media, {
            ratio: 1,
            disableMachineWatermark: true,
            width: 80,
            height: 80,
          })
        ),
      }
    }, [props.media, props.imageRatio])

    return (
      <>
        <div
          className={classNames(
            styles.mainWrapper,
            props.slidesWrapperClassName
          )}
        >
          {!props.hideShareButton && (
            <div className={styles.shareButtonWrapper}>
              <ShareButton
                machine={props.machine}
                positionList='bottom'
                locale={props.locale}
                variant='large'
              />
            </div>
          )}

          <Typography variant='body2' className={styles.slideCounter}>
            {activeIndex + 1}/{props.media.length}
          </Typography>

          <IconButton className={styles.navigationPrev} size='small'>
            <ArrowBackIcon fontSize='inherit' />
          </IconButton>

          <IconButton className={styles.navigationNext} size='small'>
            <ArrowForwardIcon fontSize='inherit' />
          </IconButton>

          <Swiper
            modules={[Thumbs, Navigation, Keyboard, Mousewheel]}
            spaceBetween={10}
            navigation={{
              nextEl: `.${styles.navigationNext}`,
              prevEl: `.${styles.navigationPrev}`,
            }}
            thumbs={{
              swiper:
                thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            keyboard={{
              enabled: true,
            }}
            mousewheel={{
              enabled: true,
            }}
            className={styles.mainSwiper}
            onSlideChange={handleSlideChange}
          >
            {images.map(({ mediaSrc, ratio }) => (
              <SwiperSlide key={mediaSrc.src} onClick={handleMainSliderClick}>
                <img
                  loading='lazy'
                  alt=''
                  {...mediaSrc}
                  className={classNames(styles.image, {
                    [styles.cover]: ratio !== 'original',
                    [styles.contain]: ratio === 'original',
                    [styles.clickable]: props.hasMediaOverlay,
                  })}
                />
                <div className='swiper-lazy-preloader' />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {props.media.length > 1 && (
          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={4}
            slidesPerView='auto'
            freeMode
            watchSlidesProgress
            className={styles.thumbsSwiper}
          >
            {thumbnails.map((src) => (
              <SwiperSlide key={src}>
                <img loading='lazy' alt='' src={src} className={styles.image} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        {props.hasMediaOverlay && (
          <MediaSliderOverlay
            open={isMediaOverlayOpen}
            media={props.media}
            onClose={handleCloseMediaOverlay}
            initialSlideIndex={activeIndex}
            watermarkImages
          />
        )}
      </>
    )
  }
)

ThumbnailSlider.displayName = 'ThumbnailSlider'
